import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "grid grid-cols-[auto_1fr] min-h-full bg-(--color-block-bg,#ffffff)" };
const _hoisted_2 = {
    class: "min-w-0 px-5 pt-6 pb-4 bg-white",
    "data-testid": "workspace-settings-view"
};
const _hoisted_3 = { class: "max-w-270 mx-auto" };
const _hoisted_4 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_5 = {
    "data-testid": "workspace-settings-title",
    class: "text-2xl sm:text-3xl font-bold text-gray-900 shrink-0 mr-60"
};
const _hoisted_6 = {
    key: 0,
    class: "flex items-center gap-3"
};
const _hoisted_7 = {
    key: 0,
    class: "flex flex-col items-center justify-center py-24",
    "data-testid": "settings-loading"
};
const _hoisted_8 = { class: "text-sm text-gray-500" };
const _hoisted_9 = {
    key: 1,
    "data-testid": "workspace-settings-error",
    class: "flex flex-col items-center justify-center py-24"
};
const _hoisted_10 = { class: "text-sm text-gray-700 mb-4" };
const _hoisted_11 = ["id", "aria-labelledby"];
import { Button, ButtonGroup, buttonGroupTabId, FormSaveButton, UnsavedChangesModal } from "@eudonet/opal";
import { computed, nextTick, onMounted, reactive, ref, shallowRef, useId, } from "vue";
import { useI18n } from "vue-i18n";
import { moduleRegistry } from "@/router/moduleRegistry";
import { logger } from "@/infrastructure/services/LoggerService";
import { getWorkspaceSettings, updateWorkspaceSettings, } from "../../infrastructure/adapters/workspaceSettingsService";
import { createEmptyWorkspaceSettings, } from "../../domain/entities/workspaceSettings";
import { validateWorkspaceIdentity, validateRna, validateSiren, validateSiret, hasErrors, } from "../../composables/useWorkspaceSettingsValidation";
import { useAppToast } from "@/shared/composables/useAppToast";
import { getWorkspaceLocale } from "@/shared/infrastructure/adapters/workspaceLocaleService";
import GlobalParametersTab from "../components/workspace-settings/GlobalParametersTab.vue";
import RfmTab from "../components/workspace-settings/RfmTab.vue";
import { useRfmSettingsForm } from "../../composables/useRfmSettingsForm";
export default /*@__PURE__*/ _defineComponent({
    __name: 'WorkspaceSettings',
    setup(__props) {
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const { t } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const state = reactive(createEmptyWorkspaceSettings());
        let originalSnapshot = JSON.stringify(state);
        const isLoading = ref(false);
        const isSaving = ref(false);
        const loadError = ref(false);
        const errors = ref({});
        // Display-only; a failed load leaves the legal-info form editable and the currency field on
        // its "not available" placeholder.
        const workspaceLocale = ref(null);
        const activeTab = ref("global");
        const panelId = useId();
        const pendingTabKey = ref(null);
        const showUnsavedChangesModal = ref(false);
        const tabs = computed(() => [
            { key: "global", label: t("workspace.settings.tabs.global") },
            { key: "rfm", label: t("workspace.settings.tabs.rfm") },
        ]);
        const isDirty = computed(() => JSON.stringify(state) !== originalSnapshot);
        const rfmForm = useRfmSettingsForm();
        // Each tab exposes the same tiny contract so the header, the dirty guard and the
        // unsaved-changes modal do not care which one is active.
        const globalForm = {
            get isDirty() {
                return isDirty.value;
            },
            get isSaving() {
                return isSaving.value;
            },
            save: handleSave,
            reset: resetGlobalForm,
        };
        const rfmFormAdapter = {
            get isDirty() {
                return rfmForm.isDirty.value;
            },
            get isSaving() {
                return rfmForm.isSaving.value;
            },
            save: async () => {
                const outcome = await rfmForm.save();
                if (outcome === "saved")
                    toastSuccess("workspace.settings.rfm.saveSuccess");
                else if (outcome === "invalid")
                    toastError("workspace.settings.errors.formInvalid");
                else
                    toastError(rfmForm.saveErrorKey.value ?? "workspace.settings.rfm.errors.saveFailed");
            },
            reset: rfmForm.reset,
        };
        const activeForm = computed(() => activeTab.value === "rfm" ? rfmFormAdapter : globalForm);
        function snapshot(settings) {
            Object.assign(state, settings);
            originalSnapshot = JSON.stringify(state);
            errors.value = {};
        }
        async function loadSettings() {
            isLoading.value = true;
            loadError.value = false;
            try {
                // Both land under the same spinner, so the currency never flashes its placeholder before
                // its value; loadWorkspaceLocale never rejects, so only the legal info can fail the page.
                const [settings] = await Promise.all([getWorkspaceSettings(), loadWorkspaceLocale()]);
                snapshot(settings);
            }
            catch (err) {
                logger.error("Failed to load workspace settings", err);
                loadError.value = true;
            }
            finally {
                isLoading.value = false;
            }
        }
        async function loadWorkspaceLocale() {
            try {
                workspaceLocale.value = await getWorkspaceLocale();
            }
            catch (err) {
                logger.error("Failed to load workspace locale", err);
            }
        }
        function onFieldUpdate(field, value) {
            state[field] = value;
            // Clear-on-correct: drop the field's error once the user edits it.
            if (errors.value[field]) {
                const next = { ...errors.value };
                delete next[field];
                errors.value = next;
            }
            // Identifier-required can auto-clear as soon as one of the three is filled.
            if (errors.value.identifiers &&
                (state.siren || state.siret || state.rna)) {
                const next = { ...errors.value };
                delete next.identifiers;
                errors.value = next;
            }
        }
        function onFieldBlur(field) {
            const next = { ...errors.value };
            delete next[field];
            let err = null;
            if (field === "siren")
                err = validateSiren(state.siren);
            else if (field === "siret")
                err = validateSiret(state.siret);
            else if (field === "rna")
                err = validateRna(state.rna);
            if (err)
                next[field] = err;
            errors.value = next;
        }
        async function handleSave() {
            const nextErrors = validateWorkspaceIdentity(state);
            errors.value = nextErrors;
            if (hasErrors(nextErrors)) {
                toastError("workspace.settings.errors.formInvalid");
                return;
            }
            isSaving.value = true;
            try {
                // Full replace: the receipt-facing fields go back as loaded, since the tax-receipt settings
                // page edits them, not this one.
                const payload = {
                    address: state.address,
                    siren: state.siren,
                    siret: state.siret,
                    rna: state.rna,
                    signatoryFunction: state.signatoryFunction,
                    signatoryName: state.signatoryName,
                    receiptObject: state.receiptObject,
                    organizationQualification: state.organizationQualification,
                };
                const saved = await updateWorkspaceSettings(payload);
                snapshot(saved);
                toastSuccess("workspace.settings.saveSuccess");
            }
            catch (err) {
                logger.error("Failed to save workspace settings", err);
                toastError("workspace.settings.saveError");
            }
            finally {
                isSaving.value = false;
            }
        }
        function activateTab(tabKey) {
            activeTab.value = tabKey;
            // The RFM grid lives behind its own endpoint — fetch it once, on first activation.
            // hasLoaded only flips when the request resolves, so isLoading guards a second in-flight call.
            if (tabKey === "rfm" && !rfmForm.hasLoaded.value && !rfmForm.isLoading.value)
                void rfmForm.load();
        }
        const tabElements = new Map();
        function setTabRef(tabKey, el) {
            const element = el && "$el" in el ? el.$el : el;
            if (element)
                tabElements.set(tabKey, element);
            else
                tabElements.delete(tabKey);
        }
        function handleTabChange(tabKey) {
            if (tabKey === activeTab.value)
                return;
            if (activeForm.value.isDirty) {
                pendingTabKey.value = tabKey;
                showUnsavedChangesModal.value = true;
                return;
            }
            activateTab(tabKey);
        }
        // The modal's focus trap hands focus back to the tab that was clicked, and OPAL's tertiary
        // :focus renders like the active state — so after Stay (or Leave) two tabs would look
        // selected. Once the trap has restored focus (before nextTick resolves), move it to the
        // tab that is actually active.
        function focusActiveTab() {
            void nextTick(() => tabElements.get(activeTab.value)?.focus());
        }
        function handleStay() {
            showUnsavedChangesModal.value = false;
            pendingTabKey.value = null;
            focusActiveTab();
        }
        function resetGlobalForm() {
            Object.assign(state, JSON.parse(originalSnapshot));
            errors.value = {};
        }
        function handleLeave() {
            showUnsavedChangesModal.value = false;
            activeForm.value.reset();
            if (pendingTabKey.value) {
                activateTab(pendingTabKey.value);
                pendingTabKey.value = null;
            }
            focusActiveTab();
        }
        onMounted(() => {
            void loadSettings();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("main", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("h1", _hoisted_5, _toDisplayString(_ctx.$t("workspace.settings.title")), 1 /* TEXT */),
                            (!isLoading.value && !loadError.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                    _createVNode(_unref(FormSaveButton), {
                                        enabled: activeForm.value.isDirty,
                                        saving: activeForm.value.isSaving,
                                        "save-label": _ctx.$t('form.saveChanges'),
                                        "saving-label": _ctx.$t('form.saving'),
                                        "disabled-hint": _ctx.$t('form.saveDisabledHint'),
                                        onSave: activeForm.value.save
                                    }, null, 8 /* PROPS */, ["enabled", "saving", "save-label", "saving-label", "disabled-hint", "onSave"])
                                ]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (isLoading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _cache[2] || (_cache[2] = _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-4xl text-gray-400 mb-4" }, null, -1 /* CACHED */)),
                                _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t("workspace.settings.loading")), 1 /* TEXT */)
                            ]))
                            : (loadError.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                    _cache[3] || (_cache[3] = _createElementVNode("i", { class: "ri-error-warning-line text-4xl text-red-400 mb-4" }, null, -1 /* CACHED */)),
                                    _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t("workspace.settings.loadError")), 1 /* TEXT */),
                                    _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        "data-testid": "workspace-settings-retry",
                                        label: _ctx.$t('workspace.settings.retry'),
                                        onClick: loadSettings
                                    }, null, 8 /* PROPS */, ["label"])
                                ]))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                    _createElementVNode("div", null, [
                                        _createVNode(_unref(ButtonGroup), {
                                            "model-value": activeTab.value,
                                            "panel-id": _unref(panelId),
                                            "overflow-label": _ctx.$t('common.tabs.next'),
                                            "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (handleTabChange($event)))
                                        }, {
                                            default: _withCtx(() => [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
                                                    return (_openBlock(), _createBlock(_unref(Button), {
                                                        key: tab.key,
                                                        ref_for: true,
                                                        ref: (el) => setTabRef(tab.key, el),
                                                        variant: "tertiary",
                                                        "data-testid": `workspace-settings-tab-${tab.key}`,
                                                        label: tab.label,
                                                        value: tab.key
                                                    }, null, 8 /* PROPS */, ["data-testid", "label", "value"]));
                                                }), 128 /* KEYED_FRAGMENT */))
                                            ]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["model-value", "panel-id", "overflow-label", "overflow-previous-label"])
                                    ]),
                                    _createElementVNode("div", {
                                        id: _unref(panelId),
                                        role: "tabpanel",
                                        "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value)
                                    }, [
                                        (activeTab.value === 'global')
                                            ? (_openBlock(), _createBlock(GlobalParametersTab, {
                                                key: 0,
                                                state: state,
                                                errors: errors.value,
                                                locale: workspaceLocale.value,
                                                "onUpdate:field": onFieldUpdate,
                                                "onBlur:field": onFieldBlur
                                            }, null, 8 /* PROPS */, ["state", "errors", "locale"]))
                                            : (activeTab.value === 'rfm')
                                                ? (_openBlock(), _createBlock(RfmTab, {
                                                    key: 1,
                                                    state: _unref(rfmForm).state,
                                                    errors: _unref(rfmForm).errors.value,
                                                    "weights-total": _unref(rfmForm).weightsTotal.value,
                                                    "preview-settings": _unref(rfmForm).previewSettings.value,
                                                    "is-loading": _unref(rfmForm).isLoading.value,
                                                    "load-error": _unref(rfmForm).loadError.value,
                                                    "onUpdate:field": _unref(rfmForm).setField,
                                                    onRetry: _unref(rfmForm).load
                                                }, null, 8 /* PROPS */, ["state", "errors", "weights-total", "preview-settings", "is-loading", "load-error", "onUpdate:field", "onRetry"]))
                                                : _createCommentVNode("v-if", true)
                                    ], 8 /* PROPS */, _hoisted_11)
                                ], 64 /* STABLE_FRAGMENT */))
                    ]),
                    _createVNode(_unref(UnsavedChangesModal), {
                        modelValue: showUnsavedChangesModal.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showUnsavedChangesModal).value = $event)),
                        title: _ctx.$t('unsavedChanges.title'),
                        message: _ctx.$t('unsavedChanges.message'),
                        "stay-label": _ctx.$t('unsavedChanges.stay'),
                        "leave-label": _ctx.$t('unsavedChanges.leave'),
                        onStay: handleStay,
                        onLeave: handleLeave
                    }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label"])
                ])
            ]));
        };
    }
});
